<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <!-- One tab per job, with an overview first: the page had grown to eight cards in one column, read top to bottom
       to find the one thing you came for. The tab is in the URL (?tab=tills) so a link can be sent. Every panel stays
       rendered (v-show), so an unlocked form and an unsaved edit survive a look at another tab. -->
  <div class="container-fluid mt-4 mb-5 vp-page">
    <h3 class="mb-1">Viva Partner Config</h3>
    <div class="vp-intro">
      CAGISTAs VIVA ISV-partnerkonto: onboarding av merchants, betalinger på nett og i kasse og kiosk, og CAGISTAs
      gebyr. Lagres kryptert i Couchbase — aldri i kildekode.
    </div>
    <div v-if="loadError" class="vp-hint vp-hint--bad mt-2">{{ loadError }}</div>

    <div class="vp-tabs mt-3" role="tablist" aria-label="Viva Partner Config" @keydown="tabKeydown">
      <button v-for="t in tabs" :key="t.key" type="button" role="tab" :id="'vp-tab-' + t.key"
              :aria-selected="tab === t.key" :aria-controls="'vp-panel-' + t.key" :tabindex="tab === t.key ? 0 : -1"
              :class="{ active: tab === t.key }" @click="go(t.key)">
        <font-awesome-icon :icon="['fas', t.icon]" /> {{ t.label }}
        <span v-if="t.badge" class="vp-tab-badge" :class="'vp-tab-badge--' + t.badge.tone">{{ t.badge.text }}</span>
        <span v-if="t.dirty" class="vp-tab-dirty" title="Ulagrede endringer"><span class="visually-hidden">ulagrede endringer</span></span>
      </button>
    </div>

    <!-- ── Oversikt ─────────────────────────────────────────────────────────────────────────────────────────── -->
    <div v-show="tab === 'overview'" id="vp-panel-overview" role="tabpanel" aria-labelledby="vp-tab-overview" class="vp-panel">
      <div class="vp-tiles">
        <a v-for="tile in overviewTiles" :key="tile.key" class="vp-tile" :class="'vp-tile--' + tile.tone"
           :href="'/vivaPartnerConfig?tab=' + tile.key" @click.prevent="go(tile.key)">
          <div class="vp-tile__head">
            <span class="vp-tile__title"><font-awesome-icon :icon="['fas', tile.icon]" /> {{ tile.title }}</span>
            <span v-if="tile.status" class="vp-pill vp-pill--sm" :class="'vp-pill--' + tile.status.tone">
              <font-awesome-icon v-if="tile.status.icon" :icon="['fas', tile.status.icon]" /> {{ tile.status.text }}</span>
          </div>
          <ul class="vp-tile__lines">
            <li v-for="(line, i) in tile.lines" :key="i" :class="'vp-line--' + line.tone">
              <span class="vp-line__icon">
                <font-awesome-icon v-if="line.tone === 'ok'" :icon="['fas', 'check']" />
                <font-awesome-icon v-else-if="line.tone === 'bad' || line.tone === 'warn'" :icon="['fas', 'exclamation-triangle']" />
              </span>
              <span>{{ line.text }}</span>
            </li>
          </ul>
          <span class="vp-tile__go">Åpne <font-awesome-icon :icon="['fas', 'chevron-right']" /></span>
        </a>
      </div>

      <!-- What is proven against PRODUCTION, and what is not yet. Replaces a «VERIFY før go-live» warning that
           outlived what it warned about: the ISV account's authorisation and the webhooks were proven against
           production on 2026-08-30/31, the first onboarding completed on 2026-10-09 (CagistaServices
           docs/viva-isv-setup-runbook.md §6a, §6c; docs/viva-legacy-merchant-switch.md). Keep it current. -->
      <div class="vp-verified mt-3">
        <font-awesome-icon :icon="['fas', 'check']" class="vp-verified__icon" />
        <span>
          <strong>Verifisert mot produksjon:</strong> ISV-tilgangen — token, opprette connected account og VIVAs
          invitasjonsside — og webhookene (30.–31.08.2026). Første fullførte onboarding 09.10.2026 (mollapizza):
          VIVAs webhook kom, og betalingskilden ble laget på merchantens eget domene. Første betaling og refusjon
          gjennom en kilde vi har laget 12.10.2026 (mollapizza): gjesten kom tilbake til eget domene, ISV-gebyret
          kom med, og refusjonen gikk gjennom.
          <strong>Ennå ikke kjørt i produksjon:</strong> å flytte en kilde når et eget domene endres etter
          onboarding — se <code>docs/viva-payment-sources.md</code>.
        </span>
      </div>
    </div>

    <!-- ── Tilkobling ───────────────────────────────────────────────────────────────────────────────────────── -->
    <!-- The partner config is ONE document with one save. Each form tab saves its own fields and sends the others as
         they were last saved, so an unsaved edit on another tab is never written by accident (save(tab)). -->
    <div v-show="tab === 'connection'" id="vp-panel-connection" role="tabpanel" aria-labelledby="vp-tab-connection" class="vp-panel">
      <div class="vp-panel__sub">Brukes av alt: onboarding, betalinger, refusjon og rapportering. Tomt oppsett →
        «Aktiver VIVA-betaling» viser «ikke konfigurert».</div>
      <config-lock ref="lockConnection" :configured="isConfigured">
        <div class="vp-grid">
          <!-- Environment and VIVA's endpoints. The environment is DERIVED from the URLs rather than stored, so it
               cannot disagree with what will actually be called. -->
          <section class="vp-card">
            <h5 class="vp-card__title">Miljø og adresser</h5>
            <div class="vp-status">
              <span class="vp-pill" :class="'vp-pill--' + environment.tone">
                <font-awesome-icon :icon="['fas', 'globe']" /> {{ environment.label }}
              </span>
              <span class="vp-status__spacer"></span>
              <span class="vp-status__fill">Fyll inn VIVA-URLer:</span>
              <button type="button" class="bo_btn_secondary vp-btn-sm" @click="applyPreset('sandbox')">Sandbox</button>
              <button type="button" class="bo_btn_secondary vp-btn-sm" @click="applyPreset('prod')">Produksjon</button>
            </div>
            <div v-if="environment.tone === 'warn'" class="vp-hint vp-hint--warn mt-1">
              URLene peker på <strong>ulike miljøer</strong>. Token-, onboarding- og rapport-URL må alle være
              sandbox eller alle produksjon — ellers svarer VIVA 401 på et kall som ser riktig ut.
            </div>
            <div class="row g-3 mt-0">
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Token-URL</label>
                <input class="form-control" type="text" v-model="form.tokenUrl"
                       placeholder="https://demo-accounts.vivapayments.com/connect/token" />
                <span class="vp-hint">Merk vertsnavnet: token ligger på <code>accounts</code>, API-ene på
                  <code>api</code>.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">OAuth-scope</label>
                <input class="form-control" type="text" v-model="form.scope"
                       placeholder="urn:viva:payments:core:api:isv urn:viva:payments:core:api:redirectcheckout" />
                <span class="vp-hint">Fra «Payment API for ISV». Feil scope gir 401/403 — svaret logges.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Onboarding-URL («Create a connected account» — ISV)</label>
                <input class="form-control" type="text" v-model="form.onboardingUrl"
                       placeholder="https://demo-api.vivapayments.com/isv/v1/accounts" />
                <span class="vp-hint vp-hint--warn">
                  Bruk <code>/isv/v1/accounts</code>, ikke <code>/platforms/v1/accounts</code>. VIVA har to
                  «create connected account»-kall — <code>platforms/v1</code> er marketplace/selger, og det er
                  den tutorialen dokumenterer. Vår er ISV.
                </span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Data Services-URL
                  <span class="vp-tag vp-tag--rep">Rapporter → Kortbetalinger</span></label>
                <input class="form-control" type="text" v-model="form.dataServicesUrl"
                       placeholder="https://demo-api.vivapayments.com/dataservices/v2/transactions/Search" />
                <span class="vp-hint">VIVA leverer kun 240 dagers historikk her. Tom URL =
                  <a href="/vivaPaymentTransactions">rapportsiden</a> viser «ikke konfigurert»; onboarding påvirkes
                  ikke.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Data Services-scope</label>
                <input class="form-control" type="text" v-model="form.dataServicesScope" />
                <span class="vp-hint">Bredere enn OAuth-scopet — krever i tillegg
                  <code>urn:viva:payments:biservices:merchantapi</code> (ikke <code>publicapi</code>, som VIVAs
                  spesifikasjon sier). Tom = standardverdien.</span>
              </div>
            </div>
          </section>

          <!-- Credentials -->
          <section class="vp-card">
            <h5 class="vp-card__title">Nøkler <span class="vp-tag">felles</span></h5>
            <div class="vp-card__sub">Fra VIVA: velg ISV-kontoen → Settings → API Access. Demo og produksjon har hvert
              sitt sett.</div>
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Client ID</label>
                <input class="form-control" type="text" v-model="form.clientId" />
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Client secret</label>
                <div style="position:relative">
                  <input :type="showSecret ? 'text' : 'password'" class="form-control" style="padding-right:38px"
                         v-model="form.clientSecret" autocomplete="new-password"
                         :placeholder="config.clientSecretSet ? '•••• (lagret — la stå tomt)' : 'Lim inn client secret'" />
                  <button type="button" @click="showSecret = !showSecret" :title="showSecret ? 'Skjul' : 'Vis'"
                          class="vp-eye">
                    <font-awesome-icon :icon="['fas', showSecret ? 'eye-slash' : 'eye']" />
                  </button>
                </div>
                <span class="vp-hint">Vises aldri igjen etter lagring. Tomt felt = behold eksisterende.</span>
                <span class="vp-hint">Ny secret? Kassene og kioskene har sin egen kopi — oppdater dem under
                  «Kasser og kiosker» etter at du har lagret.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Reseller ID</label>
                <input class="form-control" type="text" v-model="form.resellerId" />
                <span class="vp-hint">Utstedes av VIVA — <strong>ikke</strong> «Reseller API credentials» fra
                  dashbordet, og ikke Client ID over. Ulik per miljø.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Reseller API Key</label>
                <input type="password" class="form-control" v-model="form.resellerApiKey" autocomplete="new-password"
                       :placeholder="config.resellerApiKeySet ? '•••• (lagret — la stå tomt)' : 'Lim inn reseller API key'" />
                <span class="vp-hint">Kreves for <strong>refusjon</strong> hos merchants onboardet som connected
                  account — de har ingen egen apiKey. Uten den feiler refusjon for dem.</span>
              </div>
            </div>
          </section>

          <!-- Webhooks -->
          <section class="vp-card">
            <h5 class="vp-card__title">Webhooks</h5>
            <div class="vp-card__sub">VIVAs server ringer oss når en betaling eller en onboarding er ferdig.</div>
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Callback-URL (webhook)</label>
                <input class="form-control" type="text" v-model="form.callbackUrl"
                       placeholder="https://cagista.services/api/public/vivaOnboardingCallback" />
                <span class="vp-hint">Sendes <strong>ikke</strong> til VIVA — webhooken registreres separat
                  (<code>POST /isv/v1/webhooks</code>); feltet står her så URL-en er skrevet ned. VIVAs server
                  ringer den, så <strong>localhost virker aldri</strong>. Uten webhook fullføres onboarding
                  likevel via «Sjekk status hos VIVA».</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Webhook-verifiseringsnøkkel (ISV-nivå)</label>
                <input class="form-control" type="text" v-model="form.webhookVerificationKey" />
                <span class="vp-hint">Utstedes av VIVA på <strong>partnernivå</strong>
                  (<code>GET /isv/v1/webhooks/token</code>), én per miljø. Brukes av callbackene som hører til
                  partnerforholdet: <code>vivaCallback</code> (merchantenes betalinger) og
                  <code>vivaOnboardingCallback</code>. Uten den nekter VIVA å registrere webhooken.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Webhook-verifiseringsnøkkel (CAGISTAs egen konto)</label>
                <input class="form-control" type="text" v-model="form.merchantWebhookVerificationKey" />
                <span class="vp-hint">En <strong>annen</strong> nøkkel enn den over — VIVA utsteder begge, og de
                  ser like ut. Denne hører til CAGISTAs egen merchant-konto, og brukes av
                  <code>storeVivaCallback</code> og <code>subscriptionVivaCallback</code>: kassasystembutikken
                  og lisensfaktureringen er CAGISTA som selger på egen konto. Hentes med
                  <code>curl -u &lt;merchantId&gt;:&lt;apiKey&gt;
                  https://www.vivapayments.com/api/messages/config/token</code>.
                  Står den tom, brukes ISV-nøkkelen — som var oppførselen før feltet fantes, og som er grunnen
                  til at VIVA svarte «URL not verified».</span>
              </div>
            </div>
          </section>
        </div>

        <div class="vp-save-row">
          <button class="bo_btn" @click="save('connection')" :disabled="!!saving">
            {{ saving === 'connection' ? 'Lagrer…' : 'Lagre' }}</button>
          <span v-if="message && messageTab === 'connection'" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
        </div>
      </config-lock>
      <!-- Outside the lock, which closes again on save: the button must stay clickable. -->
      <div v-if="tillReminder && tillCount('OUTDATED')" class="vp-reminder">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ tillCount('OUTDATED') }} {{ tillCount('OUTDATED') === 1 ? 'avdeling har' : 'avdelinger har' }}
          fortsatt de gamle ISV-nøklene i kassene og kioskene.</span>
        <button type="button" class="bo_btn_secondary vp-btn-sm" @click="go('tills')">Gå til Kasser og kiosker</button>
      </div>
    </div>

    <!-- ── Onboarding ───────────────────────────────────────────────────────────────────────────────────────── -->
    <div v-show="tab === 'onboarding'" id="vp-panel-onboarding" role="tabpanel" aria-labelledby="vp-tab-onboarding" class="vp-panel">
      <div class="vp-panel__sub">«Aktiver VIVA-betaling» oppretter en connected account hos VIVA og tar merchanten
        gjennom bank/KYC. Onboarding-URL-en står under <a href="/vivaPartnerConfig?tab=connection"
        @click.prevent="go('connection')">Tilkobling</a>, sammen med de andre VIVA-adressene.</div>
      <config-lock ref="lockOnboarding" :configured="isConfigured">
        <div class="vp-grid">
          <section class="vp-card">
            <h5 class="vp-card__title">Retur og betalingskilde</h5>
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Return-URL (Backoffice /vivaOnboarding)</label>
                <input class="form-control" type="text" v-model="form.returnUrl"
                       placeholder="https://backoffice.cagista.com/vivaOnboarding" />
                <span class="vp-hint">Nettleser-redirect, så localhost går fint. Må være samme vertsnavn som
                  merchanten er innlogget på — cookies følger vertsnavnet.</span>
              </div>
              <div class="col-12">
                <label class="form-label mb-1 vp-label">Storefront-domene (mal)</label>
                <input class="form-control" type="text" v-model="form.storefrontDomainTemplate"
                       placeholder="{merchant}.tastyorder.no" />
                <span class="vp-hint">
                  Verten gjesten sendes tilbake til etter betaling. <strong>Kun vertsnavn</strong> —
                  VIVA avviser både protokoll og sti. <code>{merchant}</code> byttes ut med merchant-slugen.
                </span>
                <span class="vp-hint">
                  Brukes til å opprette merchantens VIVA-<em>betalingskilde</em> ved onboarding, som er der
                  retur-adressene faktisk ligger (ordren har ingen success-URL, bare en kildekode).
                  <strong>Står feltet tomt, lages ingen kilde</strong> — da havner gjesten på VIVAs egen side
                  i stedet for i butikken, og ordren blir aldri opprettet.
                </span>
              </div>
            </div>
          </section>

          <section class="vp-card">
            <h5 class="vp-card__title">Merkevare på VIVAs onboarding-side</h5>
            <div class="vp-card__sub">Dette er <strong>CAGISTAs</strong> merkevare, ikke merchantens — det vises på
              siden <em>merchanten</em> ser hos VIVA.</div>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label mb-1 vp-label">Partnernavn <span class="vp-req">*</span></label>
                <input class="form-control" type="text" v-model="form.partnerName" placeholder="CAGISTA" />
                <span class="vp-hint">Tom = «CAGISTA».</span>
              </div>
              <div class="col-md-6">
                <label class="form-label mb-1 vp-label">Logo-URL <span class="vp-req">*</span></label>
                <input class="form-control" type="text" v-model="form.logoUrl"
                       placeholder="https://cagista.com/logo.png" />
                <span class="vp-hint">VIVA henter den selv — må være offentlig tilgjengelig.</span>
              </div>
              <div class="col-md-6">
                <label class="form-label mb-1 vp-label">Primærfarge (valgfritt)</label>
                <input class="form-control" type="text" v-model="form.primaryColor" placeholder="#f30b0c" />
              </div>
              <div class="col-md-6">
                <label class="form-label mb-1 vp-label">Landkode</label>
                <input class="form-control" type="text" v-model="form.countryCode" placeholder="NO" maxlength="2" />
                <span class="vp-hint">ISO-3166 alpha-2 for merchantens adresse. Tom = NO.</span>
              </div>
            </div>
          </section>
        </div>

        <div class="vp-save-row">
          <button class="bo_btn" @click="save('onboarding')" :disabled="!!saving">
            {{ saving === 'onboarding' ? 'Lagrer…' : 'Lagre' }}</button>
          <span v-if="message && messageTab === 'onboarding'" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
        </div>
      </config-lock>
    </div>

    <!-- ── Kasser og kiosker ────────────────────────────────────────────────────────────────────────────────── -->
    <!-- The tills and kiosks put the ISV pair in EVERY payment they hand VIVA's app — from the location's viva block,
         not from this config (CagistaServices VivaTillPaymentService). So a new ISV secret at VIVA has to go out to
         every location after it is saved, or their card payments fail. VIVA's Data Services scope came only with a
         secret generated after it existed (2026-10-15). «Oppdater alle» asks VIVA for a token with the SAVED pair first
         and writes nothing if VIVA refuses it; a location on another VIVA environment, or not connected, is left alone.
         No lock: nothing here is a field, and the button confirms. -->
    <div v-show="tab === 'tills'" id="vp-panel-tills" role="tabpanel" aria-labelledby="vp-tab-tills" class="vp-panel">
      <section v-if="!loading" class="vp-card vp-cap">
        <h5 class="vp-card__title">Kasser og kiosker med VIVA <span class="vp-tag">ISV-nøklene i hver kassebetaling</span></h5>
        <div class="vp-card__sub">
          Kassene og kioskene sender ISV Client ID og secret til VIVA-appen i hver betaling, fra avdelingens
          VIVA-oppsett — ikke fra Tilkobling. Lager du en ny secret hos VIVA:
        </div>
        <ol class="vp-steps">
          <li>Lim den inn under <a href="/vivaPartnerConfig?tab=connection" @click.prevent="go('connection')">Tilkobling</a>
            og lagre.</li>
          <li>Trykk «Oppdater alle utdaterte» her — ellers feiler kortbetalingene i kassene.</li>
          <li>Ta en nettordre og en betaling i kassen, og refunder begge (kjøreboken §2g).</li>
        </ol>

        <div v-if="tillSetups.loading" class="vp-hint">Henter…</div>
        <div v-else-if="tillSetups.loadError" class="vp-hint vp-hint--bad">{{ tillSetups.loadError }}</div>
        <template v-else>
          <div class="vp-till-summary">
            <span class="vp-pill" :class="tillCount('OUTDATED') ? 'vp-pill--warn' : 'vp-pill--ok'">
              <font-awesome-icon :icon="['fas', tillCount('OUTDATED') ? 'exclamation-triangle' : 'check']" />
              {{ tillCount('OUTDATED') ? tillCount('OUTDATED') + ' utdatert' : 'Ingen utdatert' }}
            </span>
            <span class="vp-till-counts">
              {{ tillSetups.locations.length }} {{ tillSetups.locations.length === 1 ? 'avdeling' : 'avdelinger' }} med
              VIVA-oppsett · {{ tillCount('CURRENT') }} med nøklene i Tilkobling<template v-if="tillSkipped"> ·
              {{ tillSkipped }} røres ikke</template>
            </span>
          </div>

          <div v-if="tillRows.length" class="vp-fee-scroll mt-2">
            <table class="vp-fee-table vp-till-table">
              <thead>
                <tr><th>Merchant</th><th>Avdeling</th><th>Miljø</th><th>VIVA-oppsett</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in tillRows" :key="row.locationId">
                  <td data-label="Merchant">{{ row.merchant }}</td>
                  <td data-label="Avdeling">{{ row.name }}<span v-if="row.shortName" class="vp-fee-pct">({{ row.shortName }})</span></td>
                  <td data-label="Miljø">{{ row.environment || '–' }}</td>
                  <td data-label="VIVA-oppsett" :class="'vp-till-state--' + row.state">{{ tillStateLabel(row) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button v-if="tillCount('CURRENT')" type="button" class="bo_btn_secondary vp-btn-sm mt-2"
                  @click="tillSetups.showAll = !tillSetups.showAll">
            {{ tillSetups.showAll ? 'Vis bare de som trenger noe' : 'Vis alle (' + tillSetups.locations.length + ')' }}
          </button>

          <div v-if="tillCount('OUTDATED')" class="mt-3 d-flex align-items-center flex-wrap" style="gap:12px">
            <button class="bo_btn" @click="updateAllTills" :disabled="tillSetups.busy">
              {{ tillSetups.busy ? 'Oppdaterer…' : 'Oppdater alle utdaterte (' + tillCount('OUTDATED') + ')' }}
            </button>
          </div>
          <div v-if="tillSetups.message" class="mt-2" :class="tillSetups.error ? 'text-danger' : 'text-success'">
            {{ tillSetups.message }}
          </div>
          <span class="vp-hint mt-2">
            Kassene og kioskene får det nye oppsettet ved neste synk — som regel innen sekunder for en enhet som er på.
            Én avdeling om gangen: Terminaler → «Kortbetaling i kasse og kiosk».
          </span>
        </template>
      </section>
    </div>

    <!-- ── Gebyrer ──────────────────────────────────────────────────────────────────────────────────────────── -->
    <div v-show="tab === 'fees'" id="vp-panel-fees" role="tabpanel" aria-labelledby="vp-tab-fees" class="vp-panel">
      <div class="vp-split">
        <div class="vp-split__side">
          <!-- The ISV fee. Money on every merchant payment, so each rate shows what it takes from a real amount: a slip
               like 15 for 0,15 is obvious before saving, and the server refuses it anyway. -->
          <section class="vp-card">
            <h5 class="vp-card__title">CAGISTAs gebyr per betaling <span class="vp-tag vp-tag--fee">ISV-gebyr</span></h5>
            <div class="vp-card__sub">
              Merchanten får oppgitt <strong>én kortsats</strong>: VIVAs sats til CAGISTA <strong>pluss</strong>
              dette gebyret. Når VIVAs sats forhandles ned, står merchantens sats fast og gebyret her
              <strong>økes</strong> med differansen. VIVA trekker det fra merchantens utbetaling — gjesten betaler
              det samme.
            </div>
            <config-lock ref="lockFees" :configured="isConfigured">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label mb-1 vp-label">Nettbestilling (Smart Checkout)</label>
                  <div style="position:relative">
                    <input class="form-control" type="text" inputmode="decimal" style="padding-right:34px"
                           v-model="form.isvFeePercentWeb" :placeholder="defaultRate('isvFeeDefaultWeb')" />
                    <span class="vp-suffix">%</span>
                  </div>
                  <span class="vp-hint" :class="{ 'vp-hint--bad': !feePreview(form.isvFeePercentWeb).ok }">
                    {{ feePreview(form.isvFeePercentWeb).text }}</span>
                  <span class="vp-hint">Gjelder fra neste nettbestilling.</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label mb-1 vp-label">Kasse og kiosk (VIVA-terminal)</label>
                  <div style="position:relative">
                    <input class="form-control" type="text" inputmode="decimal" style="padding-right:34px"
                           v-model="form.isvFeePercentTerminal" :placeholder="defaultRate('isvFeeDefaultTerminal')" />
                    <span class="vp-suffix">%</span>
                  </div>
                  <span class="vp-hint" :class="{ 'vp-hint--bad': !feePreview(form.isvFeePercentTerminal).ok }">
                    {{ feePreview(form.isvFeePercentTerminal).text }}</span>
                  <span class="vp-hint">Kassene og kioskene henter satsen når appen starter og ved hver
                    VIVA-betaling — en endring gjelder fra neste betaling på hver enhet.</span>
                </div>
                <div class="col-12">
                  <span class="vp-hint">Mellom 0 og 1 %, høyst tre desimaler (0,125). Tomt felt = standardsatsen.
                    Avrundes til nærmeste øre per betaling. Endringer logges med hvem som lagret.</span>
                  <span class="vp-hint">Gjelder alle merchants <strong>uten egen sats</strong> — se «Merchants med egen
                    sats» under.</span>
                </div>

                <div class="col-12">
                  <div class="vp-subhead">Kilde i ISV-kontoen</div>
                  <div class="vp-card__sub mb-0">Skiller kanalene i VIVAs transaksjonsliste for ISV-kontoen:
                    nettbetalinger står alltid på <code>Default</code> (VIVA tillater ikke egne ISV-kilder for nett), kasse
                    og kiosk på koden under.</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label mb-1 vp-label">Kildekode for kasse og kiosk</label>
                  <input class="form-control" type="text" v-model="form.terminalResellerSourceCode" placeholder="Default"
                         maxlength="20" autocomplete="off" />
                  <span v-if="!sourceCodeOk" class="vp-hint vp-hint--bad">Bare bokstaver og tall, f.eks. 4651.</span>
                </div>
                <div class="col-md-6">
                  <span class="vp-hint mt-md-4">Lag en butikk i <strong>ISV-kontoen</strong> (ikke merchantens):
                    Sales → Physical Payments → Stores → Add Store, f.eks. «CAGISTA kasse og kiosk», og lim inn koden
                    øverst til venstre i dialogen. Tomt = <code>Default</code> også for kasse og kiosk. Enhetene henter
                    koden ved neste betaling.</span>
                </div>
              </div>

              <div class="vp-save-row">
                <button class="bo_btn" @click="save('fees')" :disabled="!!saving">
                  {{ saving === 'fees' ? 'Lagrer…' : 'Lagre' }}</button>
                <span v-if="message && messageTab === 'fees'" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
              </div>
            </config-lock>
          </section>

          <!-- Merchants whose own rate overrides the platform's (CagistaServices VivaIsvFeeOverrides). Read-only here:
               one place to edit — the Merchant page, with that merchant selected. -->
          <section class="vp-card">
            <h5 class="vp-card__title">Merchants med egen sats <span class="vp-tag">overstyrer plattformsatsene</span></h5>
            <div v-if="isvOverridesError" class="vp-hint vp-hint--bad">{{ isvOverridesError }}</div>
            <div v-else-if="!isvOverridesLoaded" class="vp-hint">Henter…</div>
            <div v-else-if="!isvOverrides.length" class="vp-hint">Ingen — alle merchants følger plattformsatsene.</div>
            <div v-else class="vp-fee-scroll">
              <table class="vp-fee-table vp-fee-table--num">
                <thead>
                  <tr><th>Merchant</th><th>Nett</th><th>Kasse og kiosk</th><th>Endret</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in isvOverrides" :key="row.merchant">
                    <th>{{ row.merchant }}</th>
                    <td v-for="channel in overrideChannels" :key="channel.own">
                      <strong v-if="row[channel.own]">{{ isvLabel(row[channel.effective]) }}</strong>
                      <span v-else class="vp-fee-pct" style="margin-left:0">plattform ({{ isvLabel(row[channel.effective]) }})</span>
                    </td>
                    <td class="vp-fee-pct" style="margin-left:0">{{ formatChanged(row) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <span class="vp-hint">Settes på Innstillinger → Merchant med merchanten valgt.</span>
          </section>
        </div>
        <!-- What a merchant pays per card payment — information for SUPER_ADMIN, read when a merchant calls about a
             deduction. Nothing here is edited. VIVA's part is the price list (the same for every merchant connected to
             CAGISTA); CAGISTA's part is the SAVED rates, as VIVA gets them. The whole of it: CagistaServices
             docs/viva-fees.md. Shown once the config has loaded, even if VIVA's price list has not: only the examples
             need it. -->
        <section v-if="!loading" class="vp-card">
          <h5 class="vp-card__title">Gebyrer per kortbetaling <span class="vp-tag vp-tag--fee">hva merchanten betaler</span></h5>
          <div class="vp-card__sub">
            For norske forbrukerkort. VIVAs pris er lik for alle merchants koblet til CAGISTA, og tallene her er
            <a href="/vivaPartnerConfig?tab=prices" @click.prevent="go('prices')">VIVAs prisliste</a> (den står også i
            merchantens self-care under <strong>Planer og priser</strong>), gjeldende fra 13.10.2026. CAGISTAs del er
            satsene som er lagret under «CAGISTAs gebyr per betaling».
          </div>

          <div class="vp-fee-scroll">
            <table class="vp-fee-table">
              <thead>
                <tr><th></th><th>VIVA</th><th>CAGISTA (ISV)</th></tr>
              </thead>
              <tbody>
                <tr><th>Nett</th><td>{{ vivaWebText }}</td>
                  <td>{{ isvLabel(config.isvFeePercentWeb) }}</td></tr>
                <tr><th>Kasse og kiosk</th><td>{{ vivaTillText }}</td>
                  <td>{{ isvLabel(config.isvFeePercentTerminal) }}</td></tr>
              </tbody>
            </table>
          </div>

          <div v-if="feeExamples.length" class="vp-subhead mt-3">Eksempler — trukket fra merchantens utbetaling</div>
          <div v-if="feeExamples.length" class="row g-3">
            <div v-for="channel in feeChannels" :key="channel.key" class="col-xl-6">
              <div class="vp-fee-scroll">
                <table class="vp-fee-table vp-fee-table--num">
                  <thead>
                    <tr><th>{{ channel.label }}</th><th>VIVA</th><th>CAGISTA</th><th>Totalt</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in feeExamples" :key="row.amountOre">
                      <th>{{ formatKr(row.amountOre) }}</th>
                      <td>{{ formatKr(row[channel.key].vivaOre) }}</td>
                      <td>{{ formatKr(row[channel.key].isvOre) }}</td>
                      <td><strong>{{ formatKr(row[channel.key].vivaOre + row[channel.key].isvOre) }}</strong>
                        <span class="vp-fee-pct">{{ sharePercent(row[channel.key].vivaOre + row[channel.key].isvOre, row.amountOre) }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <ul class="vp-fee-notes mt-3 mb-0">
            <li><strong>Sjekk en betaling:</strong> i merchantens VIVA er «Beløp» − «Betalt til din konto» lik VIVAs
              pris + «ISV Beløp». Kr 10 i kassen 14.10.2026: 0,37 = 0,36 (minimum) + 0,01.</li>
            <li><strong>3D Secure</strong> er bankens bekreftelse når gjesten betaler på nett (bankapp eller BankID).
              VIVA tar kr 0,36 per forsøk, også når betalingen feiler. Til gjengjeld tar kortutstederen tapet hvis et
              stjålet kort brukes, ikke restauranten.</li>
            <li><strong>«Interchange»</strong> på en betaling er inkludert i satsen for norske forbrukerkort. VIVA viser
              det, men legger det ikke til.</li>
            <li><strong>Utenlandske kort og bedriftskort</strong> koster {{ otherCardsText }} hos VIVA, pluss CAGISTAs
              andel. Interchange avhenger av kortet, så det kan ikke regnes ut på forhånd.</li>
            <li><strong>Eldre betalinger:</strong> mollapizzas nettpris var 2,19 % før 13.10.2026.</li>
            <li><strong>Et tomt gebyrfelt betyr standardsatsen</strong> ({{ isvLabel(config.isvFeeDefaultWeb) }} på
              nett, {{ isvLabel(config.isvFeeDefaultTerminal) }} i kasse og kiosk). Skal CAGISTA ikke ta noe, må
              feltet stå på 0.</li>
            <li>Hele gjennomgangen: <code>CagistaServices/docs/viva-fees.md</code>.</li>
          </ul>
        </section>

      </div>
    </div>

    <!-- ── VIVAs prisliste ──────────────────────────────────────────────────────────────────────────────────── -->
    <!-- VIVA's price to merchants connected to CAGISTA — the same for all of them (VIVA, 2026-10-15). Its own lock and its
         own save: it is not part of the partner config. cagista.com prints these PLUS the platform ISV rate, never these
         alone (CagistaServices VivaCardPrices, /api/store/cardFees). The rows are fixed by VIVA's list; only the figures
         are edited. -->
    <div v-show="tab === 'prices'" id="vp-panel-prices" role="tabpanel" aria-labelledby="vp-tab-prices" class="vp-panel">
      <section class="vp-card vp-cap">
        <h5 class="vp-card__title">VIVAs pris til CAGISTA-merchants <span class="vp-tag vp-tag--fee">innkjøpspris</span></h5>
        <div class="vp-card__sub">
          Lik for alle merchants koblet til CAGISTA. cagista.com viser sluttprisen — disse tallene pluss plattformens
          ISV-sats under <a href="/vivaPartnerConfig?tab=fees" @click.prevent="go('fees')">Gebyrer</a> — aldri disse
          alene. En endring vises på cagista.com innen fem minutter.
        </div>
        <div v-if="!cardPrices.rows.length" class="vp-hint" :class="{ 'vp-hint--bad': cardPrices.error }">
          {{ cardPrices.message || 'Henter…' }}</div>
        <config-lock v-else ref="priceLock" :configured="true">
          <div class="vp-fee-scroll">
            <table class="vp-fee-table vp-price-table">
              <thead>
                <tr><th>Betalingsmåte</th><th>Kasse og kiosk</th><th>Nett</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in cardPrices.rows" :key="row.key">
                  <th>{{ row.label }}</th>
                  <td v-for="ch in priceChannels" :key="ch" :data-label="ch === 'present' ? 'Kasse og kiosk' : 'Nett'">
                    <div v-if="row[ch].offered" class="vp-price-cell">
                      <span v-if="row.interchange && row[ch].hasPercent" class="vp-price-word">Interchange +</span>
                      <span v-if="row[ch].hasPercent" class="vp-price-field">
                        <input class="form-control form-control-sm" type="text" inputmode="decimal"
                               v-model="priceForm[row.key][ch].percent" />
                        <span class="vp-price-unit">%</span>
                      </span>
                      <template v-if="row[ch].hasMinimum">
                        <span class="vp-price-word">minst kr</span>
                        <span class="vp-price-field vp-price-field--kr">
                          <input class="form-control form-control-sm" type="text" inputmode="decimal"
                                 v-model="priceForm[row.key][ch].minimum" />
                        </span>
                      </template>
                      <template v-if="row[ch].hasFixed">
                        <span class="vp-price-word">{{ row[ch].hasPercent ? '+ kr' : 'kr' }}</span>
                        <span class="vp-price-field vp-price-field--kr">
                          <input class="form-control form-control-sm" type="text" inputmode="decimal"
                                 v-model="priceForm[row.key][ch].fixed" />
                        </span>
                      </template>
                    </div>
                    <span v-else class="vp-fee-pct" style="margin-left:0">—</span>
                  </td>
                </tr>
                <tr>
                  <th>3D Secure, per kortbetaling på nett</th>
                  <td data-label="Kasse og kiosk"><span class="vp-fee-pct" style="margin-left:0">—</span></td>
                  <td data-label="Nett">
                    <div class="vp-price-cell">
                      <span class="vp-price-word">kr</span>
                      <span class="vp-price-field vp-price-field--kr">
                        <input class="form-control form-control-sm" type="text" inputmode="decimal"
                               v-model="priceForm.threeDSecure" />
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="vp-save-row">
            <button class="bo_btn" @click="saveCardPrices" :disabled="cardPrices.saving">
              {{ cardPrices.saving ? 'Lagrer…' : 'Lagre prisliste' }}</button>
            <span v-if="cardPrices.message" :class="cardPrices.error ? 'text-danger' : 'text-success'">{{ cardPrices.message }}</span>
          </div>
          <span class="vp-hint">
            {{ cardPrices.updatedBy ? 'Sist endret ' + formatTime(cardPrices.updatedTime) + ' av ' + cardPrices.updatedBy + '.'
               : 'Aldri endret — dette er VIVAs liste slik den kom (oktober 2026).' }}
            Prosent med komma, beløp i kroner. Et tomt felt går tilbake til VIVAs liste.
          </span>
        </config-lock>
      </section>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import SettingsService from "@/services/settings/SettingsService";

const FEE_EXAMPLE_AMOUNTS_ORE = [1000, 10000, 30000, 50000];

/** URL and code are English like every other route here; only what SUPER_ADMIN reads is Norwegian. */
const TABS = ["overview", "connection", "onboarding", "tills", "fees", "prices"];

/**
 * Which fields of the ONE partner config each tab edits. Every field is in exactly one tab, and save(tab) sends that
 * tab's fields as typed and the rest as last saved.
 */
const FORM_TABS = {
  connection: ["clientId", "clientSecret", "resellerId", "resellerApiKey", "tokenUrl", "scope", "onboardingUrl",
    "dataServicesUrl", "dataServicesScope", "callbackUrl", "webhookVerificationKey", "merchantWebhookVerificationKey"],
  onboarding: ["returnUrl", "storefrontDomainTemplate", "partnerName", "logoUrl", "primaryColor", "countryCode"],
  fees: ["isvFeePercentWeb", "isvFeePercentTerminal", "terminalResellerSourceCode"]
};
const FORM_FIELDS = [].concat(FORM_TABS.connection, FORM_TABS.onboarding, FORM_TABS.fees);
/** Never sent back by the server; blank on save = keep the stored one. */
const SECRET_FIELDS = ["clientSecret", "resellerApiKey"];
const FEE_FIELDS = ["isvFeePercentWeb", "isvFeePercentTerminal"];
const LOCK_REFS = { connection: "lockConnection", onboarding: "lockOnboarding", fees: "lockFees" };

export default {
  name: "VivaPartnerConfig",
  components: { LoadingSpinner, ConfigLock, ConfirmDialogue },
  computed: {
    /** Mirrors CagistaServices' rule; the code goes into the terminal app's intent URI. */
    sourceCodeOk() {
      const code = (this.form.terminalResellerSourceCode || "").trim();
      return code === "" || /^[A-Za-z0-9]{1,20}$/.test(code);
    },
    tabs() {
      const outdated = this.tillCount("OUTDATED");
      const tabs = [
        { key: "overview", label: "Oversikt", icon: "th-large" },
        { key: "connection", label: "Tilkobling", icon: "plug",
          badge: !this.loading && (!this.isConfigured || this.savedEnvironment.tone === "warn") ? { text: "!", tone: "bad" } : null },
        { key: "onboarding", label: "Onboarding", icon: "user-plus",
          badge: !this.loading && this.isConfigured && !this.saved.storefrontDomainTemplate ? { text: "!", tone: "bad" } : null },
        { key: "tills", label: "Kasser og kiosker", icon: "cash-register",
          badge: outdated ? { text: String(outdated), tone: "bad" } : null },
        { key: "fees", label: "Gebyrer", icon: "percent" },
        { key: "prices", label: "VIVAs prisliste", icon: "tags" }
      ];
      return tabs.map((t) => Object.assign(t, { dirty: this.dirty(t.key) }));
    },
    /**
     * One tile per tab: what is set up, and what needs a look. From what is SAVED, not from what is typed — the overview
     * reports what VIVA, the tills and the web are actually using.
     */
    overviewTiles() {
      return [this.connectionTile, this.onboardingTile, this.tillsTile, this.feesTile, this.pricesTile]
        .map((tile) => Object.assign({}, tile, { tone: this.tileTone(tile) }));
    },
    connectionTile() {
      const s = this.saved;
      const env = this.savedEnvironment;
      const lines = [];
      if (env.tone === "warn") {
        lines.push({ text: "URLene peker på ulike miljøer — VIVA svarer 401", tone: "bad" });
      }
      lines.push(this.config.clientSecretSet ? { text: "Client secret lagret", tone: "ok" }
        : { text: "Client secret mangler", tone: "bad" });
      lines.push(this.config.resellerApiKeySet ? { text: "Reseller API Key lagret", tone: "ok" }
        : { text: "Reseller API Key mangler — refusjon feiler for connected merchants", tone: "bad" });
      if ((s.onboardingUrl || "").includes("/platforms/")) {
        lines.push({ text: "Onboarding-URL peker på platforms/v1 — skal være isv/v1", tone: "bad" });
      }
      lines.push(s.webhookVerificationKey ? { text: "Webhook-nøkkel (ISV) satt", tone: "ok" }
        : { text: "Webhook-nøkkel (ISV) mangler", tone: "bad" });
      if (!s.merchantWebhookVerificationKey) {
        lines.push({ text: "Webhook-nøkkel for CAGISTAs egen konto mangler — ISV-nøkkelen brukes", tone: "warn" });
      }
      lines.push({ text: s.dataServicesUrl ? "Rapportering: Data Services-URL satt" : "Rapportering ikke satt opp", tone: "muted" });
      // Which VIVA it talks to is the first thing to know, so it is the tile's badge, in the same colours as on the tab.
      return {
        key: "connection", icon: "plug", title: "Tilkobling", lines,
        status: this.isConfigured ? { text: env.label, tone: env.tone, icon: "globe" } : { text: "Ikke konfigurert", tone: "off" }
      };
    },
    onboardingTile() {
      const s = this.saved;
      const lines = [];
      lines.push(s.storefrontDomainTemplate ? { text: "Betalingskilde på " + s.storefrontDomainTemplate, tone: "ok" }
        : { text: "Storefront-domene mangler — ingen betalingskilde lages", tone: "bad" });
      lines.push(s.returnUrl ? { text: "Merchanten kommer tilbake til " + this.hostOf(s.returnUrl), tone: "ok" }
        : { text: "Return-URL mangler", tone: "bad" });
      lines.push(s.logoUrl ? { text: "Merkevare: " + (s.partnerName || "CAGISTA") + ", med logo", tone: "ok" }
        : { text: "Logo-URL mangler på VIVAs onboarding-side", tone: "bad" });
      lines.push({ text: "Landkode " + (s.countryCode || "NO"), tone: "muted" });
      const missing = lines.some((l) => l.tone === "bad");
      return {
        key: "onboarding", icon: "user-plus", title: "Onboarding", lines,
        status: missing ? { text: "Mangler noe", tone: "warn" } : { text: "Klar", tone: "ok" }
      };
    },
    tillsTile() {
      const t = this.tillSetups;
      const outdated = this.tillCount("OUTDATED");
      let status;
      const lines = [];
      if (t.loading) {
        status = { text: "Henter…", tone: "off" };
      } else if (t.loadError) {
        status = { text: "Kunne ikke hentes", tone: "warn" };
        lines.push({ text: t.loadError, tone: "bad" });
      } else {
        status = !t.locations.length ? { text: "Ingen med VIVA", tone: "off" }
          : (outdated ? { text: outdated + " utdatert", tone: "warn" } : { text: "Alle oppdatert", tone: "ok" });
        lines.push({ text: t.locations.length + (t.locations.length === 1 ? " avdeling" : " avdelinger") + " med VIVA-oppsett", tone: "plain" });
        if (outdated) {
          lines.push({ text: outdated + " med gamle ISV-nøkler — trykk «Oppdater alle utdaterte»", tone: "bad" });
        }
        if (this.tillCount("CURRENT")) {
          lines.push({ text: this.tillCount("CURRENT") + " med nøklene i Tilkobling", tone: "ok" });
        }
        if (this.tillSkipped) {
          lines.push({ text: this.tillSkipped + " røres ikke (annet VIVA-miljø eller ikke koblet til)", tone: "muted" });
        }
      }
      return { key: "tills", icon: "cash-register", title: "Kasser og kiosker", status, lines };
    },
    feesTile() {
      const lines = [
        { text: "CAGISTA på nett: " + this.isvLabel(this.config.isvFeePercentWeb), tone: "plain" },
        { text: "CAGISTA i kasse og kiosk: " + this.isvLabel(this.config.isvFeePercentTerminal), tone: "plain" },
        { text: "Kildekode kasse og kiosk: " + (this.config.terminalResellerSourceCode || "Default"), tone: "muted" }
      ];
      if (this.isvOverridesError) {
        lines.push({ text: this.isvOverridesError, tone: "bad" });
      } else if (this.isvOverridesLoaded) {
        const n = this.isvOverrides.length;
        lines.push({ text: n ? n + (n === 1 ? " merchant" : " merchants") + " med egen sats" : "Ingen merchants med egen sats", tone: "muted" });
      }
      return { key: "fees", icon: "percent", title: "Gebyrer", status: null, lines };
    },
    pricesTile() {
      const lines = [];
      if (!this.cardPrices.rows.length) {
        lines.push(this.cardPrices.error ? { text: this.cardPrices.message, tone: "bad" } : { text: "Henter…", tone: "muted" });
      } else {
        lines.push({ text: "Nett: " + this.vivaWebText, tone: "plain" });
        lines.push({ text: "Kasse og kiosk: " + this.vivaTillText, tone: "plain" });
        lines.push({ text: "Norske forbrukerkort · " + (this.cardPrices.updatedBy
          ? "sist endret " + this.formatTime(this.cardPrices.updatedTime) + " av " + this.cardPrices.updatedBy
          : "VIVAs liste slik den kom (oktober 2026)"), tone: "muted" });
      }
      return { key: "prices", icon: "tags", title: "VIVAs prisliste", status: null, lines };
    },
    /** The locations that need a look; all of them when asked. */
    tillRows() {
      const rows = this.tillSetups.locations || [];
      return this.tillSetups.showAll ? rows : rows.filter((r) => r.state !== "CURRENT");
    },
    /** Left alone by «Oppdater alle»: another VIVA environment, not connected, or unreadable. */
    tillSkipped() {
      return this.tillCount("OTHER_ENVIRONMENT") + this.tillCount("NOT_CONNECTED") + this.tillCount("UNREADABLE");
    },
    overrideChannels() {
      return [{ own: "web", effective: "effectiveWeb" }, { own: "terminal", effective: "effectiveTerminal" }];
    },
    feeChannels() {
      return [{ key: "web", label: "Nett" }, { key: "till", label: "Kasse og kiosk" }];
    },
    /**
     * What each example amount costs the merchant: VIVA's price (the price list) plus CAGISTA's SAVED rates (as VIVA
     * gets them — the server reports the default when a field is blank). Rounded to the nearest øre, as VIVA and
     * CagistaServices' VivaIsvFee do. Empty until both have loaded.
     */
    feeExamples() {
      const web = this.thousandths(this.config.isvFeePercentWeb);
      const till = this.thousandths(this.config.isvFeePercentTerminal);
      const VIVA_REFERENCE = this.vivaReference;
      if (web === null || till === null || !VIVA_REFERENCE) {
        return [];
      }
      const pct = (amountOre, thousandths) => Math.round((amountOre * thousandths) / 100000);
      return FEE_EXAMPLE_AMOUNTS_ORE.map((amountOre) => ({
        amountOre,
        web: {
          vivaOre: pct(amountOre, VIVA_REFERENCE.webThousandths) + VIVA_REFERENCE.webFixedOre + VIVA_REFERENCE.threeDSecureOre,
          isvOre: pct(amountOre, web)
        },
        till: {
          vivaOre: Math.max(pct(amountOre, VIVA_REFERENCE.tillThousandths), VIVA_REFERENCE.tillMinimumOre),
          isvOre: pct(amountOre, till)
        }
      }));
    },
    priceChannels() {
      return ["present", "notPresent"];
    },
    /**
     * VIVA's price for Norwegian consumer cards as SAVED (not as typed): percentages in thousandths of a percent
     * (1190 = 1,19 %), amounts in øre. Null until the price list has loaded.
     */
    vivaReference() {
      const row = this.cardPrices.rows.find((r) => r.key === "noConsumer");
      if (!row) {
        return null;
      }
      return {
        webThousandths: this.thousandths(row.notPresent.percent) || 0,
        webFixedOre: row.notPresent.fixedOre || 0,
        threeDSecureOre: this.cardPrices.threeDSecureOre || 0,
        tillThousandths: this.thousandths(row.present.percent) || 0,
        tillMinimumOre: row.present.minimumOre || 0
      };
    },
    vivaWebText() {
      const r = this.vivaReference;
      return r ? this.thousandthsText(r.webThousandths) + " + " + this.formatKr(r.webFixedOre) + " + "
          + this.formatKr(r.threeDSecureOre) + " for 3D Secure" : "–";
    },
    vivaTillText() {
      const r = this.vivaReference;
      return r ? this.thousandthsText(r.tillThousandths) + ", minst " + this.formatKr(r.tillMinimumOre) : "–";
    },
    /** «interchange + 0,44 % i kassen og interchange + 0,79 % + kr 1,80 på nett», from the saved list. */
    otherCardsText() {
      const row = this.cardPrices.rows.find((r) => r.key === "otherCards");
      if (!row) {
        return "interchange + et påslag";
      }
      return "interchange + " + this.thousandthsText(this.thousandths(row.present.percent)) + " i kassen og interchange + "
          + this.thousandthsText(this.thousandths(row.notPresent.percent)) + " + " + this.formatKr(row.notPresent.fixedOre)
          + " på nett";
    },
    /** The price list as typed differs from the one last loaded or saved. */
    pricesDirty() {
      return this.savedPriceForm !== "" && JSON.stringify(this.priceForm) !== this.savedPriceForm;
    },
    // Already configured (→ starts locked): a client secret is stored, or a client ID was loaded.
    isConfigured() { return !!(this.config.clientSecretSet || this.config.clientId); },
    /** The environment the URLs being edited point at — moves as you type. */
    environment() {
      return this.environmentOf([this.form.tokenUrl, this.form.onboardingUrl, this.form.dataServicesUrl]);
    },
    /** The environment the SAVED URLs point at — what is actually called. */
    savedEnvironment() {
      return this.environmentOf([this.saved.tokenUrl, this.saved.onboardingUrl, this.saved.dataServicesUrl]);
    }
  },
  data() {
    const empty = {};
    FORM_FIELDS.forEach((k) => { empty[k] = ""; });
    return {
      loading: true,
      loadError: "",
      tab: "overview",
      config: {},
      form: Object.assign({}, empty),
      // The form as last loaded or saved — what the other tabs send on a save, and what «unsaved» is measured against.
      saved: Object.assign({}, empty),
      isvOverrides: [],
      isvOverridesLoaded: false,
      isvOverridesError: "",
      showSecret: false,
      saving: "",          // the tab being saved, or ""
      message: "",
      messageTab: "",      // the tab whose save (or preset) the message belongs to
      error: false,
      // VIVA's price to CAGISTA merchants — CagistaServices VivaCardPrices. rows as the server sends them (VIVA's own
      // figures, saved or default); priceForm is the editable copy, in kroner and percent with commas.
      cardPrices: { rows: [], threeDSecureOre: 0, updatedBy: "", updatedTime: "", saving: false, message: "", error: false },
      priceForm: { threeDSecure: "" },
      savedPriceForm: "",
      // The ISV pair in every location's viva block — CagistaServices VivaTillPaymentService.fleet. No secret in it.
      tillSetups: { locations: [], counts: {}, loading: true, loadError: "", showAll: false, busy: false, message: "", error: false },
      tillReminder: false
    };
  },
  created() {
    const wanted = String(this.$route.query.tab || "");
    if (TABS.includes(wanted)) this.tab = wanted;
  },
  mounted() {
    this.loadCardPrices();
    this.loadConfig();
    this.loadIsvOverrides();
    this.loadTillSetups();
  },
  methods: {
    go(tab) {
      if (!TABS.includes(tab)) return;
      if (tab !== this.tab) {
        this.tab = tab;
        // In the URL so a link can be sent, and so a reload stays on the same tab.
        this.$router.replace({ path: "/vivaPartnerConfig", query: tab === "overview" ? {} : { tab } });
      }
      // From a tile low on the overview, or a phone where the strip scrolls sideways: bring the tab into view.
      this.$nextTick(() => {
        const el = document.getElementById("vp-tab-" + tab);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", inline: "nearest" });
      });
    },
    /** Arrow keys, Home and End move between tabs, as a tablist does. */
    tabKeydown(event) {
      const i = TABS.indexOf(this.tab);
      const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: TABS.length - 1 }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      const tab = TABS[(next + TABS.length) % TABS.length];
      this.go(tab);
      this.$nextTick(() => { const el = document.getElementById("vp-tab-" + tab); if (el) el.focus(); });
    },
    /** A tab has edits that are not saved. */
    dirty(tab) {
      if (tab === "prices") return this.pricesDirty;
      const fields = FORM_TABS[tab];
      if (!fields) return false;
      return fields.some((k) => (SECRET_FIELDS.includes(k)
        ? !!this.form[k]
        : String(this.form[k] === undefined || this.form[k] === null ? "" : this.form[k]) !== String(this.saved[k] || "")));
    },
    tileTone(tile) {
      if (tile.lines.some((l) => l.tone === "bad")) return "bad";
      if (tile.lines.some((l) => l.tone === "warn")) return "warn";
      return "plain";
    },
    hostOf(url) {
      try {
        return new URL(url).host;
      } catch (e) {
        return url;
      }
    },
    /**
     * Which VIVA environment the URLs point at. DERIVED, never stored - a stored flag could disagree with the URLs that
     * are actually called, which is the failure this is meant to make visible. A mix is its own state: token on demo
     * with the API on prod answers 401 on a request that looks perfectly right.
     */
    environmentOf(list) {
      const urls = list.map((u) => (u || "").trim()).filter((u) => u.length > 0);
      if (!urls.length) return { label: "Ingen URLer satt", tone: "off" };
      const demo = urls.filter((u) => u.includes("demo")).length;
      if (demo === urls.length) return { label: "Sandbox", tone: "sandbox" };
      if (demo === 0) return { label: "Produksjon", tone: "prod" };
      return { label: "Blandet miljø", tone: "warn" };
    },
    /** The form as the server reports the config: secrets blank, the two ISV rates written the Norwegian way. */
    formFromConfig(config) {
      const f = {};
      FORM_FIELDS.forEach((k) => {
        if (SECRET_FIELDS.includes(k)) {
          f[k] = "";
        } else if (FEE_FIELDS.includes(k)) {
          f[k] = this.toComma(config[k]);
        } else {
          f[k] = config[k] === undefined || config[k] === null ? "" : config[k];
        }
      });
      return f;
    },
    loadConfig() {
      SettingsService.getVivaPartnerConfig().then(
        (response) => {
          this.config = response.data || {};
          this.form = this.formFromConfig(this.config);
          this.saved = this.formFromConfig(this.config);
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.loadError = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    /**
     * Saves one tab. The config is one document and the server stores what it is sent, so the other tabs' fields go
     * as last saved — an edit left unsaved on another tab stays in its form, still marked, and is not written here.
     */
    save(tab) {
      const fields = FORM_TABS[tab];
      const payload = Object.assign({}, this.saved);
      fields.forEach((k) => { payload[k] = this.form[k]; });
      this.saving = tab;
      this.message = "";
      this.messageTab = tab;
      this.error = false;
      SettingsService.saveVivaPartnerConfig(payload).then(
        (response) => {
          this.saving = "";
          this.config = response.data || {};
          const fresh = this.formFromConfig(this.config);
          this.saved = fresh;
          // As the server normalised them («0,1» comes back «0,10»), and the secrets out of the DOM.
          fields.forEach((k) => { this.form[k] = fresh[k]; });
          this.showSecret = false;
          this.error = false;
          this.message = "Lagret ✓ — tas i bruk umiddelbart (ingen restart).";
          const lock = this.$refs[LOCK_REFS[tab]];
          if (lock) lock.relock();   // re-lock after a successful save
          if (tab === "connection") {
            // A new secret is in use for the web at once, but the tills carry their own copy: say so right here.
            this.loadTillSetups().then(() => { this.tillReminder = true; });
          }
        },
        (error) => {
          this.saving = "";
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    /**
     * Fills VIVA's three URLs in one go. Only VIVA's own endpoints - never the callback/return URLs, which
     * are ours and differ per deployment, nor the credentials, which differ per environment and must be
     * pasted deliberately.
     */
    applyPreset(env) {
      const host = env === 'prod'
        ? { accounts: 'https://accounts.vivapayments.com', api: 'https://api.vivapayments.com' }
        : { accounts: 'https://demo-accounts.vivapayments.com', api: 'https://demo-api.vivapayments.com' };
      this.form.tokenUrl = host.accounts + '/connect/token';
      this.form.onboardingUrl = host.api + '/isv/v1/accounts';
      this.form.dataServicesUrl = host.api + '/dataservices/v2/transactions/Search';
      if (!this.form.scope) {
        this.form.scope = 'urn:viva:payments:core:api:isv urn:viva:payments:core:api:redirectcheckout';
      }
      this.messageTab = 'connection';
      this.message = 'URLer fylt inn (' + (env === 'prod' ? 'produksjon' : 'sandbox') + ') — husk å lagre.';
      this.error = false;
    },
    toComma(value) {
      return value === undefined || value === null ? "" : String(value).replace(".", ",");
    },
    defaultRate(key) {
      return this.toComma(this.config[key] || "");
    },
    /**
     * What a rate takes from a kr 100 and a kr 1 000 payment, or why it would be refused. The SERVER is the
     * check (CagistaServices VivaIsvFee.parsePercent, same rules); this only shows a slip before saving.
     * Thousandths of a percent keep the arithmetic in integers, as the server's does.
     */
    feePreview(text) {
      const t = String(text === undefined || text === null ? "" : text).replace("%", "").replace(",", ".").trim();
      if (!t) {
        return { ok: true, text: "Tomt — standardsatsen brukes." };
      }
      if (!/^\d+(\.\d+)?$/.test(t)) {
        return { ok: false, text: "Skriv et tall i prosent, f.eks. 0,15." };
      }
      if ((t.split(".")[1] || "").replace(/0+$/, "").length > 3) {
        return { ok: false, text: "Høyst tre desimaler." };
      }
      const thousandths = Math.round(Number(t) * 1000);
      if (thousandths > 1000) {
        return { ok: false, text: "Over 1 % — skriv 0,15 for 0,15 %." };
      }
      const fee = (amountMinor) => Math.round((amountMinor * thousandths) / 100000);
      return { ok: true, text: "kr 100 → " + this.formatOre(fee(10000)) + " · kr 1 000 → " + this.formatOre(fee(100000)) };
    },
    loadTillSetups() {
      this.tillSetups.loadError = "";
      return SettingsService.getVivaTillSetups().then(
        (response) => {
          this.applyTillSetups(response.data);
          this.tillSetups.loading = false;
        },
        (error) => {
          console.log(error);
          this.tillSetups.loadError = this.errMsg(error, "Kunne ikke hente avdelingene med VIVA i kasse og kiosk.");
          this.tillSetups.loading = false;
        }
      );
    },
    applyTillSetups(data) {
      this.tillSetups.locations = Array.isArray(data && data.locations) ? data.locations : [];
      this.tillSetups.counts = (data && data.counts) || {};
    },
    tillCount(state) {
      return (this.tillSetups.counts && this.tillSetups.counts[state]) || 0;
    },
    tillStateLabel(row) {
      switch (row.state) {
        case "CURRENT": return "På plass";
        case "OUTDATED": return "Utdatert — gamle ISV-nøkler";
        case "OTHER_ENVIRONMENT": return "Annet VIVA-miljø — røres ikke her";
        case "NOT_CONNECTED": return "Ikke koblet til CAGISTA hos VIVA — røres ikke";
        default: return "Kunne ikke leses";
      }
    },
    async updateAllTills() {
      const n = this.tillCount("OUTDATED");
      const ok = await this.$refs.confirmDialogue.show({
        title: "Oppdater alle kasser og kiosker",
        message: "ISV-nøklene som er lagret under Tilkobling skrives inn i VIVA-oppsettet til " + n
          + (n === 1 ? " avdeling" : " avdelinger")
          + ". CAGISTA spør VIVA først om nøklene virker — godtar ikke VIVA dem, endres ingenting. Kassene og kioskene får "
          + "det nye oppsettet ved neste synk. Avdelinger på et annet VIVA-miljø eller uten kobling røres ikke.",
        okButton: "Oppdater " + n,
        cancelButton: "Avbryt"
      });
      if (!ok) {
        return;
      }
      this.tillSetups.busy = true;
      this.tillSetups.message = "";
      SettingsService.updateAllVivaTillSetups().then(
        (response) => {
          const d = response.data || {};
          const failed = Array.isArray(d.failed) ? d.failed : [];
          if (d.fleet && !d.fleet.error) {
            this.applyTillSetups(d.fleet);
          } else {
            this.loadTillSetups();
          }
          this.tillSetups.busy = false;
          this.tillSetups.error = failed.length > 0;
          const n = d.updated || 0;
          this.tillSetups.message = n + (n === 1 ? " avdeling" : " avdelinger") + " oppdatert."
            + (failed.length
              ? " Feilet: " + failed.map((f) => f.merchant + " / " + f.name + " — " + f.error).join("; ")
                + " Trykk igjen for å prøve dem på nytt."
              : "");
        },
        (error) => {
          console.log(error);
          this.tillSetups.busy = false;
          this.tillSetups.error = true;
          this.tillSetups.message = this.errMsg(error, "Kunne ikke oppdatere kassene og kioskene.");
        }
      );
    },
    /** Every merchant with a rate of its own — CagistaServices VivaIsvFeeOverrides. */
    loadIsvOverrides() {
      SettingsService.getVivaIsvFeeOverrides().then(
        (response) => {
          this.isvOverrides = Array.isArray(response.data) ? response.data : [];
          this.isvOverridesLoaded = true;
        },
        (error) => {
          console.log(error);
          this.isvOverridesError = this.errMsg(error, "Kunne ikke hente merchants med egen sats.");
          this.isvOverridesLoaded = true;
        }
      );
    },
    formatChanged(row) {
      const d = new Date(row.updatedTime);
      const when = isNaN(d.getTime()) ? "" : d.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });
      return [when, row.updatedBy].filter(Boolean).join(" · ");
    },
    /** A rate as the server reports it («0.15», «0.00») in thousandths of a percent, or null when absent. */
    thousandths(rate) {
      if (rate === undefined || rate === null || String(rate).trim() === "") {
        return null;
      }
      const n = Number(String(rate).replace(",", "."));
      return Number.isFinite(n) ? Math.round(n * 1000) : null;
    },
    /** CAGISTA's rate in words: «0,10 %», or «ingenting» for 0. */
    isvLabel(rate) {
      const t = this.thousandths(rate);
      if (t === null) {
        return "–";
      }
      return t === 0 ? "ingenting" : this.toComma(rate) + " %";
    },
    formatKr(ore) {
      return "kr " + (ore / 100).toFixed(2).replace(".", ",");
    },
    sharePercent(feeOre, amountOre) {
      return "(" + ((feeOre * 100) / amountOre).toFixed(2).replace(".", ",") + " %)";
    },
    formatOre(ore) {
      return ore < 100 ? ore + " øre" : "kr " + (ore / 100).toFixed(2).replace(".", ",");
    },
    loadCardPrices() {
      SettingsService.getVivaCardPrices().then(
        (response) => this.applyCardPrices(response.data),
        (error) => {
          console.log(error);
          this.cardPrices.error = true;
          this.cardPrices.message = this.errMsg(error, "Kunne ikke hente VIVAs prisliste.");
        }
      );
    },
    applyCardPrices(data) {
      const rows = Array.isArray(data && data.rows) ? data.rows : [];
      const kr = (ore) => (ore === undefined || ore === null ? "" : (ore / 100).toFixed(2).replace(".", ","));
      const form = { threeDSecure: kr(data.threeDSecureOre) };
      rows.forEach((row) => {
        form[row.key] = {};
        this.priceChannels.forEach((ch) => {
          const c = row[ch] || {};
          form[row.key][ch] = { percent: this.toComma(c.percent), minimum: kr(c.minimumOre), fixed: kr(c.fixedOre) };
        });
      });
      this.priceForm = form;
      this.savedPriceForm = JSON.stringify(form);
      this.cardPrices.rows = rows;
      this.cardPrices.threeDSecureOre = data.threeDSecureOre;
      this.cardPrices.updatedBy = data.updatedBy || "";
      this.cardPrices.updatedTime = data.updatedTime || "";
    },
    /**
     * Kroner as typed («1,80») to whole øre («180»), or the text unchanged when it is not an amount — the server then
     * refuses the save and says which field. Blank stays blank (= VIVA's list).
     */
    toOre(text) {
      const t = String(text === undefined || text === null ? "" : text).replace(",", ".").trim();
      if (!t) {
        return "";
      }
      return /^\d+(\.\d{1,2})?$/.test(t) ? String(Math.round(Number(t) * 100)) : t;
    },
    saveCardPrices() {
      const rows = {};
      this.cardPrices.rows.forEach((row) => {
        rows[row.key] = {};
        this.priceChannels.forEach((ch) => {
          const f = this.priceForm[row.key][ch];
          rows[row.key][ch] = { percent: f.percent, minimumOre: this.toOre(f.minimum), fixedOre: this.toOre(f.fixed) };
        });
      });
      this.cardPrices.saving = true;
      this.cardPrices.message = "";
      SettingsService.saveVivaCardPrices({ rows, threeDSecureOre: this.toOre(this.priceForm.threeDSecure) }).then(
        (response) => {
          this.applyCardPrices(response.data);
          this.cardPrices.saving = false;
          this.cardPrices.error = false;
          this.cardPrices.message = "Lagret — cagista.com viser de nye sluttprisene innen fem minutter.";
          if (this.$refs.priceLock) this.$refs.priceLock.relock();
        },
        (error) => {
          console.log(error);
          this.cardPrices.saving = false;
          this.cardPrices.error = true;
          this.cardPrices.message = this.errMsg(error, "Kunne ikke lagre prislisten.");
        }
      );
    },
    /** «0,84 %», «1,19 %», «0,125 %» — at least two decimals. */
    thousandthsText(thousandths) {
      const fixed = ((thousandths || 0) / 1000).toFixed(3);
      return (fixed.endsWith("0") ? fixed.slice(0, -1) : fixed).replace(".", ",") + " %";
    },
    formatTime(iso) {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
/* The whole width, as Ordrer: the cards sit side by side instead of one long column. */
.vp-page { max-width: 1860px; margin: 0 auto; }
.vp-label { font-size: 0.85rem; color: var(--bo-muted); }
.vp-intro { font-style: italic; color: var(--bo-muted); font-size: 0.9rem; }
.vp-req { color: #b00020; }

/*
  The tabs, in Website Builder's look: which one you are on is carried by the brand edge along the top, the card
  surface, ink instead of muted text, and the weight. On a narrow screen the strip scrolls sideways rather than
  wrapping into a second row of tabs.
*/
.vp-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--bo-line);
  overflow-x: auto;
  scrollbar-width: thin;
}
.vp-tabs button {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 7px;
  white-space: nowrap;
  border: 1px solid var(--bo-line);
  border-top: 3px solid transparent;
  border-bottom: 0;
  background: var(--bo-surface-2);
  color: var(--bo-muted);
  border-radius: 8px 8px 0 0;
  padding: 8px 16px 9px;
  font-size: .92rem;
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
}
.vp-tabs button:hover:not(.active) { color: var(--bo-ink); background: var(--bo-surface); }
.vp-tabs button:focus-visible { outline: 2px solid var(--bo-brand-ink); outline-offset: -2px; }
.vp-tabs button.active {
  background: var(--bo-surface);
  border-top-color: var(--bo-brand-ink);
  color: var(--bo-ink);
  font-weight: 600;
}
.vp-tabs button.active svg { color: var(--bo-brand-ink); }
/* Needs a look: the count of outdated tills, or «!» for a setup that is missing something. */
.vp-tab-badge {
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .72rem; font-weight: 700; line-height: 1;
}
.vp-tab-badge--bad { background: #b00020; color: #fff; }
/* Edits on this tab that are not saved. */
.vp-tab-dirty { width: 8px; height: 8px; border-radius: 50%; background: #ECB109; display: inline-block; }

.vp-panel { padding-top: 16px; }
.vp-panel__sub { color: var(--bo-muted); font-size: 0.85rem; margin-bottom: 4px; max-width: 1100px; }
.vp-panel__sub a, .vp-card__sub a, .vp-steps a { color: var(--bo-brand-ink); }

/* Cards side by side on a wide screen, one under the other on a narrow one. */
.vp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
  gap: 16px;
  align-items: start;
}
.vp-grid > .vp-card, .vp-split .vp-card { margin-top: 0; }
/* Gebyrer: what CAGISTA sets on the left, what a merchant then pays on the right — the wider column, so the two example
   tables fit side by side. On a phone the rate comes first. */
/* minmax(0, …): a grid column otherwise grows to its widest unbreakable row, and the fee tables have nowrap amounts. */
.vp-split { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; align-items: start; }
@media (min-width: 1200px) {
  .vp-split { grid-template-columns: minmax(0, 2fr) minmax(0, 3fr); }
}
.vp-split__side { display: flex; flex-direction: column; gap: 16px; }
/* A single table reads badly stretched across 1800px. */
.vp-cap { max-width: 1200px; }
.vp-panel > .vp-card.vp-cap { margin-top: 0; }

.vp-save-row { margin-top: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.vp-reminder {
  margin-top: 12px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  border: 1px solid var(--bo-bad-line); background: var(--bo-bad-bg); color: var(--bo-alert-ink);
  border-radius: 8px; padding: 8px 12px; font-size: .88rem;
}

/* ── Oversikt ── */
.vp-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  gap: 16px;
}
.vp-tile {
  display: flex; flex-direction: column; gap: 10px;
  border: 1px solid var(--bo-line);
  border-left: 4px solid var(--bo-line);
  border-radius: 10px;
  padding: 14px 16px 12px;
  background: var(--bo-surface);
  color: var(--bo-ink);
  text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease;
}
.vp-tile:hover, .vp-tile:focus-visible {
  color: var(--bo-ink); text-decoration: none;
  transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0, 0, 0, .08);
}
.vp-tile:focus-visible { outline: 2px solid var(--bo-brand-ink); outline-offset: 2px; }
.vp-tile--warn { border-left-color: #ECB109; }
.vp-tile--bad { border-left-color: #b00020; }
.vp-tile__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.vp-tile__title { font-weight: 600; font-size: 1rem; display: inline-flex; align-items: center; gap: 8px; }
.vp-tile__title svg { color: var(--bo-brand-ink); }
.vp-tile__lines { list-style: none; margin: 0; padding: 0; font-size: .85rem; line-height: 1.4; }
.vp-tile__lines li { display: flex; gap: 8px; align-items: baseline; padding: 2px 0; float: none; }
.vp-line__icon { flex: 0 0 14px; width: 14px; font-size: .78rem; }
.vp-line--ok .vp-line__icon { color: var(--bo-success-ink); }
.vp-line--warn .vp-line__icon, .vp-line--warn { color: var(--bo-brand-ink); }
.vp-line--bad .vp-line__icon, .vp-line--bad { color: var(--bo-alert-ink); }
.vp-line--muted { color: var(--bo-muted); }
.vp-tile__go { margin-top: auto; align-self: flex-end; font-size: .8rem; color: var(--bo-brand-ink); }

/* One card per purpose, so the eye can skip whole blocks it is not there for. */
.vp-card {
  border: 1px solid var(--bo-line);
  border-radius: 10px;
  padding: 16px 18px;
  background: var(--bo-surface);
  margin-top: 1rem;
}
.vp-card__title { font-size: 1rem; margin-bottom: 2px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vp-card__sub { color: var(--bo-muted); font-size: 0.82rem; margin-bottom: 12px; }
.vp-subhead { font-weight: 600; font-size: 0.92rem; margin-top: 6px; }
.vp-steps { font-size: 0.85rem; padding-left: 1.2rem; margin-bottom: 12px; }
.vp-steps li { margin-bottom: 2px; float: none; }

/* What the section drives, so a field is never orphaned from its purpose. */
.vp-tag {
  font-size: 0.72rem; font-weight: 400; color: var(--bo-muted);
  background: var(--bo-surface-2); border-radius: 20px; padding: 2px 10px;
}
.vp-tag--rep { color: var(--bo-success-ink); background: var(--bo-ok-bg); }
.vp-tag--fee { color: var(--bo-brand-ink); background: var(--bo-brand-tint); }

.vp-status { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
.vp-status__spacer { flex: 1 1 auto; }
.vp-status__fill { color: var(--bo-muted); font-size: 0.82rem; }
.vp-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.82rem; border-radius: 20px; padding: 3px 12px;
  border: 1px solid transparent;
}
.vp-pill--sm { font-size: 0.75rem; padding: 2px 10px; }
.vp-pill--ok { background: var(--bo-ok-bg); color: #2E7D32; border-color: #b7e2c0; }
.vp-pill--off { background: var(--bo-surface-2); color: var(--bo-muted); border-color: var(--bo-line); }
.vp-pill--sandbox { background: var(--bo-info-bg); color: #2a5d9f; border-color: #c6d9f2; }
.vp-pill--prod { background: var(--bo-brand-tint); color: #8A6800; border-color: #ffd591; }
.vp-pill--warn { background: var(--bo-bad-bg); color: #b00020; border-color: #f5a3a3; }

/* The tills' copy of the ISV pair: the count first, then only the locations that need a look. */
.vp-till-summary { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vp-till-counts { color: var(--bo-muted); font-size: 0.82rem; }
.vp-till-state--CURRENT { color: #2E7D32; }
.vp-till-state--OUTDATED { color: #b00020; font-weight: 600; }
.vp-till-state--OTHER_ENVIRONMENT, .vp-till-state--NOT_CONNECTED, .vp-till-state--UNREADABLE { color: var(--bo-brand-ink); }
/* On a phone each location becomes a block — merchant and location on one line, the state under it. Doubled class:
   the .vp-fee-table rules come later in this file and would otherwise win. */
@media (max-width: 767px) {
  .vp-till-table.vp-fee-table thead { display: none; }
  .vp-till-table.vp-fee-table, .vp-till-table.vp-fee-table tbody, .vp-till-table.vp-fee-table tr { display: block; width: 100%; }
  .vp-till-table.vp-fee-table tr { border-bottom: 1px solid var(--bo-line); padding: 6px 0; }
  .vp-till-table.vp-fee-table td { display: inline; border-bottom: none; padding: 0 6px 0 0; }
  .vp-till-table.vp-fee-table td[data-label="Merchant"] { font-weight: 600; }
  .vp-till-table.vp-fee-table td[data-label="Miljø"] { color: var(--bo-muted); font-size: 0.78rem; }
  .vp-till-table.vp-fee-table td[data-label="VIVA-oppsett"] { display: block; padding-top: 2px; }
}

/* Small enough to sit on a status line without competing with Lagre. */
.vp-btn-sm { min-width: 0; padding: 3px 12px; font-size: 0.82rem; }

.vp-hint { display: block; color: var(--bo-muted); font-size: 0.78rem; margin-top: 2px; }
.vp-hint--warn { color: var(--bo-brand-ink); }
.vp-hint--bad { color: #b00020; }

/* The unit sits inside the field, so «0,15» can never be read as a fraction or kroner. */
.vp-suffix {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  color: var(--bo-muted); pointer-events: none;
}
/* A status, not a warning: green border on the ok tint, text in the normal colour so it reads in both themes. */
.vp-verified {
  display: flex; gap: 8px; align-items: flex-start;
  font-size: 0.85rem; line-height: 1.45;
  border: 1px solid #b7e2c0; background: var(--bo-ok-bg); border-radius: 8px; padding: 8px 12px;
}
.vp-verified__icon { color: #2E7D32; margin-top: 3px; flex: 0 0 auto; }
/* Half the page on a wide screen: a paragraph across 1800px is hard to read. Full width on a phone. */
@media (min-width: 992px) {
  .vp-verified { max-width: 50%; }
}

/* The fee tables: plain lines in the theme's colours (Bootstrap's .table paints its own backgrounds). */
.vp-fee-scroll { overflow-x: auto; }
.vp-fee-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.vp-fee-table th, .vp-fee-table td { padding: 5px 8px; border-bottom: 1px solid var(--bo-line); text-align: left; vertical-align: top; }
.vp-fee-table thead th { color: var(--bo-muted); font-weight: 600; font-size: 0.78rem; }
.vp-fee-table tbody th { font-weight: 600; white-space: nowrap; }
.vp-fee-pct { color: var(--bo-muted); font-size: 0.75rem; margin-left: 4px; white-space: nowrap; }
/* An amount never breaks between «kr» and the figure; on a phone the share drops under the total instead. */
.vp-fee-table--num th, .vp-fee-table--num td { white-space: nowrap; }
@media (max-width: 575px) {
  .vp-fee-table--num .vp-fee-pct { display: block; margin-left: 0; }
}
.vp-fee-notes { font-size: 0.82rem; line-height: 1.45; padding-left: 1.1rem; }
.vp-fee-notes li { margin-bottom: 4px; float: none; }

/* VIVA's price list: each cell is the price written as a sentence with the figures as fields in it. */
.vp-price-table tbody th { white-space: normal; width: 34%; }
.vp-price-table td { min-width: 170px; }
/* A phone has no room for three columns of fields: each method becomes a block, its two channels under it. */
@media (max-width: 767px) {
  .vp-price-table thead { display: none; }
  .vp-price-table, .vp-price-table tbody, .vp-price-table tr, .vp-price-table tbody th, .vp-price-table td {
    display: block; width: 100%; min-width: 0;
  }
  .vp-price-table tr { border-bottom: 1px solid var(--bo-line); padding: 6px 0; }
  .vp-price-table th, .vp-price-table td { border-bottom: none; padding: 3px 0; }
  .vp-price-table td::before {
    content: attr(data-label); display: block; color: var(--bo-muted); font-size: 0.75rem; margin-bottom: 2px;
  }
}
.vp-price-cell { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 6px; }
.vp-price-word { color: var(--bo-muted); font-size: 0.8rem; white-space: nowrap; }
.vp-price-field { position: relative; display: inline-block; width: 76px; }
.vp-price-field input { padding-right: 22px; }
.vp-price-field--kr { width: 68px; }
.vp-price-field--kr input { padding-right: 8px; }
.vp-price-unit { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: var(--bo-muted); font-size: 0.8rem; pointer-events: none; }

.vp-eye {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  border: none; background: none; color: var(--bo-muted); cursor: pointer; padding: 0; line-height: 1;
}
</style>
